import { forwardRef } from 'react';
import type { ReactNode, FunctionComponent, Ref } from 'react';
import styled, { css } from 'styled-components';

import type {
  BaseProps,
  ExcludeStrict,
  ForwardProps,
  NoChildrenProp,
  TestIdProp
} from '../../types';
import Grid from '../Grid';
import { StyledLabel } from '../Label';
import { useBreakpoint, useConsolidatedRef, useTestIds, useTheme } from '../../hooks';
import { defaultThemeProp } from '../../theme';
import ShowMoreLess from '../ShowMoreLess';
import { withTestIds } from '../../utils';
import NoValue from '../NoValue/NoValue';

import { getFieldValueListTestIds, getFieldValueItemTestIds } from './FieldValueList.test-ids';

export type FieldValueVariant = 'inline' | 'stacked';

interface BaseFieldValueListItem extends TestIdProp {
  name: string;
  id?: string;
  variant?: FieldValueVariant;
}

export type FieldValueListItemProps =
  | (BaseFieldValueListItem & {
      value?: ExcludeStrict<ReactNode, 'string'>;
      truncate?: never;
    })
  | (BaseFieldValueListItem & {
      value: string;
      truncate?: boolean;
    });

export interface FieldValueListProps extends BaseProps, NoChildrenProp, TestIdProp {
  /** The set of field values to render in the list. */
  fields: FieldValueListItemProps[];
  /**
   * Determines if the field and values should be displayed on the same line, or stacked with the field above the value.
   * @default 'inline'
   */
  variant?: FieldValueVariant | 'value-comparison';
}

export const StyledFieldName = styled.dt`
  word-break: break-word;
`;

export const StyledFieldValue = styled.dd`
  word-break: break-word;
`;

/**
 * `display: contents` wrapper around a `dt + dd` pair in the inline
 * variant. Lets CSS grid on the parent `<dl>` still see dt/dd as direct
 * layout items, while giving UI Designer / Inspector a single hit
 * target that covers label + value together. Exposed so that callers
 * which previously targeted `dt:not(:last-of-type)` / `dd:not(:last-of-type)`
 * can target the row instead.
 */
export const StyledInlineFieldValueRow = styled.div`
  display: contents;
`;

export const StyledStackedFieldValue = styled.div``;

export type StyledFieldValueListProps = Pick<FieldValueListProps, 'variant'>;

export const StyledFieldValueList = styled.dl<StyledFieldValueListProps>(({ variant, theme }) => {
  const {
    components: {
      'field-value-list': {
        inline: { detached }
      }
    }
  } = theme;
  return css`
    width: 100%;

    ${variant === 'value-comparison' &&
    css`
      dt {
        width: 100%;
      }
      && dd {
        text-align: end;
        padding-inline-start: calc(2 * ${theme.base.spacing});

        /* Unset negative margin from Details to prevent field value misalignment */
        margin: unset;
      }
      ${StyledLabel} {
        max-width: unset;
      }
      ${StyledInlineFieldValueRow}:not(:last-child) > dd,
      ${StyledInlineFieldValueRow}:not(:last-child) > dt {
        border-bottom: 0.0625rem solid ${theme.base.palette['border-line']};
        padding-bottom: calc(0.5 * ${theme.base.spacing});
      }
    `}

    ${detached &&
    variant === 'inline' &&
    css`
      dt {
        width: 100%;
        padding-inline-end: calc(${theme.base.spacing});
      }
      ${StyledLabel} {
        max-width: unset;
      }
      ${StyledInlineFieldValueRow}:not(:last-child) > dd,
      ${StyledInlineFieldValueRow}:not(:last-child) > dt,
      ${StyledInlineFieldValueRow}:has(+ ${StyledStackedFieldValue}) > dd,
      ${StyledInlineFieldValueRow}:has(+ ${StyledStackedFieldValue}) > dt,
      ${StyledStackedFieldValue}:has(+ ${StyledInlineFieldValueRow}),
      ${StyledStackedFieldValue}:not(:last-of-type) {
        border-bottom: 0.0625rem dashed ${theme.base.palette['border-line']};
        padding-bottom: calc(0.5 * ${theme.base.spacing});
      }
    `}
  `;
});

StyledFieldValueList.defaultProps = defaultThemeProp;

export const FieldValueItem = withTestIds(
  ({ testId, name, value, variant = 'inline', truncate = true }: FieldValueListItemProps) => {
    const testIds = useTestIds(testId, getFieldValueItemTestIds);

    const displayValue =
      typeof value === 'string' && truncate ? (
        <ShowMoreLess lines={3}>{value}</ShowMoreLess>
      ) : (
        value
      );

    const labelAndValue = (
      <>
        <StyledLabel data-testid={testIds.name} as={StyledFieldName}>
          {name}
        </StyledLabel>
        <StyledFieldValue data-testid={testIds.value}>
          {value ? displayValue : <NoValue />}
        </StyledFieldValue>
      </>
    );

    return variant === 'stacked' ? (
      <StyledStackedFieldValue data-testid={testIds.root} data-editable-anchor>
        {labelAndValue}
      </StyledStackedFieldValue>
    ) : (
      <StyledInlineFieldValueRow data-editable-anchor>{labelAndValue}</StyledInlineFieldValueRow>
    );
  },
  getFieldValueItemTestIds
);

const FieldValueList: FunctionComponent<FieldValueListProps & ForwardProps> = forwardRef(
  function FieldValueList(
    { testId, fields, variant = 'inline', ...restProps }: FieldValueListProps,
    ref: Ref<HTMLDivElement>
  ) {
    const testIds = useTestIds(testId, getFieldValueListTestIds);

    const fieldValueListRef = useConsolidatedRef<HTMLDivElement>(ref);

    const isXSContentWidthOrAbove = useBreakpoint('xs', {
      breakpointRef: fieldValueListRef,
      themeProp: 'content-width'
    });

    const {
      components: {
        'field-value-list': {
          inline: { detached }
        }
      }
    } = useTheme();

    return (
      <Grid
        data-testid={testIds.root}
        {...restProps}
        ref={fieldValueListRef}
        container={{
          cols:
            ['inline', 'value-comparison'].includes(variant) && isXSContentWidthOrAbove
              ? '16ch minmax(0, 1fr)'
              : 'minmax(0, 1fr)',
          colGap: !detached && variant !== 'value-comparison' ? 2 : 0,
          rowGap: !detached && variant !== 'value-comparison' ? 1 : 0.5
        }}
        as={StyledFieldValueList}
        role='none'
        variant={isXSContentWidthOrAbove ? variant : 'stacked'}
      >
        {fields.map(
          ({ testId: fieldTestId, id, name, value, truncate = true, variant: fieldVariant }) => {
            const sharedProps: BaseFieldValueListItem = {
              name,
              variant:
                (fieldVariant ?? variant) === 'stacked' || !isXSContentWidthOrAbove
                  ? 'stacked'
                  : 'inline'
            };

            if (typeof value === 'string') {
              return (
                <FieldValueItem
                  testId={fieldTestId}
                  key={id ?? name}
                  {...sharedProps}
                  value={value}
                  truncate={truncate}
                />
              );
            }

            return (
              <FieldValueItem
                testId={fieldTestId}
                key={id ?? name}
                {...sharedProps}
                value={value}
              />
            );
          }
        )}
      </Grid>
    );
  }
);

export default withTestIds(FieldValueList, getFieldValueListTestIds);
